import React, {
    useCallback,
    useEffect,
    useState,
    MouseEvent,
    memo,
} from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import Translate from '@client/components/shared/translation/Translate';
import MailOpenedIcon from '@client/components/shared/svgIcons/MailOpened';
import { Button, ButtonColor } from '@pegasus/ui-components/components/button';
import {
    getParamEmailProvider,
    getParamSendingConfirmationEmail,
} from '@client/redux/modules/successOverlay/selectors';
import { sendConfirmationEmail } from '@client/redux/modules/successOverlay/actions';
import { getEmail } from '@client/redux/modules/customer/selectors';
import { getAlert } from '@client/redux/modules/successOverlay/selectors';
import { track } from '@client/helpers';
import {
    ALERT_ERROR,
    ALERT_SUCCESS,
} from '@client/redux/modules/successOverlay/constants';
import { Wrapper, ResendEmailWrapper } from './DoiSuccessContent.styles';
import { IntlShape, injectIntl } from 'react-intl';
import ModalHeader from '../../modal-header';
import MessageTile from '../../message-tile';
import DoiSuccessResendLink from '../DoiSuccessResend';
import DoiSuccessAlert from '../DoiSuccessAlert';

const RESEND_LIMIT = 3;

interface StateProps {
    email?: string;
    emailProvider?: string;

    isSendingConfirmationEmail: boolean;
    alert?: {
        type: typeof ALERT_SUCCESS | typeof ALERT_ERROR;
        message: string;
    };
}

interface DispatchProps {
    sendConfirmationEmail: (args: {
        errorMessage: string;
        successMessage: string;
    }) => Promise<void>;
}

type DoiSuccessProps = StateProps & DispatchProps & { intl: IntlShape };

const instructions = ['instruction1', 'instruction2', 'instruction3'];

export const DoiSuccessContent = memo(
    ({
        email,
        emailProvider,
        alert,
        sendConfirmationEmail,
        isSendingConfirmationEmail,
        intl,
    }: DoiSuccessProps) => {

        const [resendCount, setResendCount] = useState<number>(0);

        const goToInbox = useCallback(() => {
            window.open(`http://www.${emailProvider}`, '_blank');
            track('doi.email.gotoInboxCta');
        }, [emailProvider]);

        const handleResendConfirmationEmail = useCallback(() => {
            sendConfirmationEmail({
                errorMessage: intl.formatMessage({
                    id: 'sendingFailed',
                }),
                successMessage: intl.formatMessage({
                    id: 'sent',
                }),
            }).then(() => {
                track('doi.email.resendCta');
                // After some tries we hide the button to prevent users going crazy
                setResendCount(resendCount + 1);
            });
        }, [sendConfirmationEmail, resendCount]);

        return (
            <>
                <Wrapper>
                    <ModalHeader
                        title={<Translate message="header" />}
                        subtitle={
                            <Translate message="sub" params={{ email }} html />
                        }
                    />
                    <MessageTile
                        icon={MailOpenedIcon}
                        title={<Translate message="instructionsHeader" />}
                        smallPrints={instructions.map(instruction => (
                            <Translate message={instruction} />
                        ))}
                    />
                    {emailProvider && (
                        <Button
                            fullWidth
                            color={ButtonColor.dark}
                            onClick={goToInbox}
                        >
                            <Translate message="gotoInbox" />
                        </Button>
                    )}
                    {alert ? (
                        <DoiSuccessAlert {...alert} />
                    ) : (
                        resendCount < RESEND_LIMIT && (
                            <ResendEmailWrapper>
                                <DoiSuccessResendLink
                                    onClick={handleResendConfirmationEmail}
                                    isLoading={isSendingConfirmationEmail}
                                />
                            </ResendEmailWrapper>
                        )
                    )}
                </Wrapper>
            </>
        );
    },
);

const mapStateToProps = (state: any) => ({
    email: getEmail(state),
    emailProvider: getParamEmailProvider(state),
    isSendingConfirmationEmail: getParamSendingConfirmationEmail(state),
    alert: getAlert(state),
});

const mapDispatchToProps = {
    sendConfirmationEmail,
};

export default compose(
    injectIntl,
    connect(
        mapStateToProps,
        mapDispatchToProps,
    ),
)(DoiSuccessContent);
